import { MarketingContent, MarketingContentType } from '../types/marketing';

/** Field keys on MarketingContent that a stage may surface for inline editing. */
export type StageFieldKey =
  | 'recordingDate'
  | 'publishDate'
  | 'guestName'
  | 'dropboxRawUrl'
  | 'dropboxArchiveUrl'
  | 'ssdPath'
  | 'youtubeUrl'
  | 'thumbnailUrl'
  | 'notes';

export interface WorkflowStage {
  id: string;
  label: string;
  description?: string;
  /** Fields surfaced inline on this stage in the detail panel. */
  fields?: StageFieldKey[];
  /** Optional label for a Phase-2 action button (integration hook). */
  actionLabel?: string;
  /** Visual color for kanban column dot + stage chip. */
  color: string;
  /** Lucide icon name (resolved at render). */
  icon: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  contentType: MarketingContentType;
  description?: string;
  stages: WorkflowStage[];
  /** When true, this template appears in the New Content modal. */
  enabled: boolean;
}

/** Podcast-style video pipeline: shoot -> dropbox -> SSD -> edit -> publish -> notify -> archive. */
export const PODCAST_VIDEO_TEMPLATE: WorkflowTemplate = {
  id: 'podcast-video-v1',
  name: 'Podcast Video',
  contentType: 'podcast-video',
  description: 'Long-form video shot, edited, published to YouTube, then archived.',
  enabled: true,
  stages: [
    {
      id: 'shoot',
      label: 'Shoot Media',
      description: 'Capture episode footage.',
      fields: ['recordingDate', 'guestName'],
      color: '#94A3B8',
      icon: 'Camera',
    },
    {
      id: 'dropbox-raw',
      label: 'Upload Raw to Dropbox',
      description: 'Move raw footage off the camera into the Dropbox raw folder.',
      fields: ['dropboxRawUrl'],
      color: '#60A5FA',
      icon: 'Cloud',
    },
    {
      id: 'ssd-download',
      label: 'Download to SSD',
      description: 'Pull raw footage to the editing SSD.',
      fields: ['ssdPath'],
      color: '#3B82F6',
      icon: 'HardDrive',
    },
    {
      id: 'edit',
      label: 'Edit in FCP',
      description: 'Cut and finish the episode in Final Cut Pro.',
      fields: ['notes'],
      color: '#F59E0B',
      icon: 'Scissors',
    },
    {
      id: 'publish',
      label: 'Publish to YouTube',
      description: 'Upload, set thumbnail, schedule or publish.',
      fields: ['youtubeUrl', 'publishDate', 'thumbnailUrl'],
      color: '#22C55E',
      icon: 'Youtube',
    },
    {
      id: 'notify',
      label: 'Send Notification Email',
      description: 'Announce the episode to the email list.',
      actionLabel: 'Mark sent',
      color: '#14B8A6',
      icon: 'Mail',
    },
    {
      id: 'archive',
      label: 'Archive Drive & Wipe',
      description: 'Upload completed drive contents to Dropbox archive, then wipe SSD.',
      fields: ['dropboxArchiveUrl'],
      color: '#166534',
      icon: 'Archive',
    },
  ],
};

/** Registry of available templates. v1 ships with podcast video only. */
export const TEMPLATES: Record<string, WorkflowTemplate> = {
  [PODCAST_VIDEO_TEMPLATE.id]: PODCAST_VIDEO_TEMPLATE,
};

export function getTemplate(templateId: string): WorkflowTemplate | undefined {
  return TEMPLATES[templateId];
}

export function getTemplateOrDefault(templateId: string): WorkflowTemplate {
  return TEMPLATES[templateId] ?? PODCAST_VIDEO_TEMPLATE;
}

/** Initial stage status map for a fresh content item: all stages 'todo'. */
export function initialStageStatuses(template: WorkflowTemplate) {
  const out: MarketingContent['stageStatuses'] = {};
  for (const s of template.stages) {
    out[s.id] = { status: 'todo' };
  }
  return out;
}

/** Find the first non-done, non-skipped stage. Falls back to the last stage. */
export function nextOpenStageId(
  template: WorkflowTemplate,
  statuses: MarketingContent['stageStatuses']
): string {
  for (const s of template.stages) {
    const st = statuses[s.id]?.status;
    if (st !== 'done' && st !== 'skipped') return s.id;
  }
  return template.stages[template.stages.length - 1]?.id ?? '';
}

export const CONTENT_TYPE_LABELS: Record<MarketingContentType, string> = {
  'podcast-video': 'Podcast Videos',
  reel: 'Reels',
  blog: 'Blog Posts',
  'email-blast': 'Email Blasts',
  ad: 'Ads',
  custom: 'Custom',
};
